import { type FC } from 'react';
import { Form } from 'react-bootstrap';

import {
	type Figure,
	FigurePosition,
	FigureSubType,
	FigureType,
	HeaderStyle
} from '@editor2/types/pageElements';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';

interface Props {
	figureProperties: Partial<Figure>;
	onChange: (_) => void;
}

const FigureCommonForm: FC<Props> = (props) => {
	const { figureProperties, onChange } = props;

	return (
		<>
			{figureProperties.figure_type === FigureType.video && (
				<Form.Group>
					<Form.Label>Subtype</Form.Label>
					<Form.Control
						as="select"
						className="select"
						value={figureProperties.figure_subtype}
						onChange={(e) => onChange({ figure_subtype: e.target.value })}>
						{Object.keys(FigureSubType).map((type) => (
							<option key={type} value={type}>
								{type}
							</option>
						))}
					</Form.Control>
				</Form.Group>
			)}

			<Form.Group>
				<Form.Check
					type="checkbox"
					checked={String(figureProperties.payload_metadata?.requires_signing) === 'true'}
					onChange={(e) =>
						onChange({
							payload_metadata: {
								...figureProperties.payload_metadata,
								requires_signing: !(
									String(figureProperties.payload_metadata?.requires_signing) === 'true'
								)
							}
						})
					}
					label="Requires signing"
				/>
				{figureProperties.figure_type === FigureType.image && (
					<Form.Check
						type="checkbox"
						checked={figureProperties.disable_expanded_view}
						onChange={() =>
							onChange({
								disable_expanded_view: !figureProperties.disable_expanded_view
							})
						}
						label="Disable expanded view"
					/>
				)}
			</Form.Group>

			<Form.Group>
				<Form.Label>Position</Form.Label>
				<Form.Control
					as="select"
					className="select"
					value={figureProperties.figure_position}
					onChange={(e) => onChange({ figure_position: e.target.value })}>
					{Object.keys(FigurePosition).map((type) => (
						<option key={type} value={type}>
							{type}
						</option>
					))}
				</Form.Control>
			</Form.Group>

			<Form.Group>
				<Form.Label>Header Style</Form.Label>
				<Form.Control
					as="select"
					className="select"
					value={figureProperties.header_style}
					onChange={(e) => onChange({ header_style: e.target.value })}>
					{Object.keys(HeaderStyle).map((type) => (
						<option key={type} value={type}>
							{type}
						</option>
					))}
				</Form.Control>
			</Form.Group>

			<Form.Group>
				<Form.Label>Number</Form.Label>
				<Form.Control
					value={figureProperties.figure_number}
					placeholder="Figure number"
					onChange={(e) => onChange({ figure_number: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Title</Form.Label>
				<Form.Control
					value={figureProperties.title}
					placeholder="Figure title"
					onChange={(e) => onChange({ title: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Instructions</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={figureProperties.instructions}
					placeholder="Figure instructions"
					onChange={(e) => onChange({ instructions: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Payload</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.payload}
					showTips={false}
					onChange={(value) => onChange({ payload: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Description</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.description}
					showTips={false}
					options={{ placeholder: 'Description (for accessibility)' }}
					onChange={(value) => onChange({ description: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Caption</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.caption}
					showTips={false}
					options={{ placeholder: 'Figure caption' }}
					onChange={(value) => onChange({ caption: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Credit(s)</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.credits}
					showTips={false}
					options={{ placeholder: 'Figure credits' }}
					onChange={(value) => onChange({ credits: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Citation</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={figureProperties.citation}
					placeholder="Figure citation"
					onChange={(e) => onChange({ citation: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Transcript URL</Form.Label>
				<Form.Control
					value={figureProperties.transcript_url}
					placeholder="Transcript URL"
					onChange={(e) => onChange({ transcript_url: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Backup URL</Form.Label>
				<Form.Control
					value={figureProperties.figure_backup_url}
					placeholder="Backup URL"
					onChange={(e) => onChange({ figure_backup_url: e.target.value })}
				/>
			</Form.Group>

			{figureProperties.figure_subtype === FigureSubType.vzaar && (
				<Form.Group>
					<Form.Label>Secret</Form.Label>
					<Form.Control
						value={figureProperties.payload_metadata?.vzaar_shared_secret}
						placeholder="Vzaar user specific secret token"
						onChange={(e) =>
							onChange({
								payload_metadata: {
									...figureProperties.payload_metadata,
									vzaar_shared_secret: e.target.value
								}
							})
						}
					/>
				</Form.Group>
			)}

			{figureProperties.figure_type === FigureType.html && (
				<Form.Group>
					<Form.Check
						type="checkbox"
						checked={figureProperties.payload_metadata?.offline}
						onChange={(e) =>
							onChange({
								payload_metadata: {
									...figureProperties.payload_metadata,
									offline: !figureProperties.payload_metadata?.offline
								}
							})
						}
						label="Offline"
					/>
				</Form.Group>
			)}
		</>
	);
};

export default FigureCommonForm;
